import { useApiClient } from "@louise/api/louise-api";
import { ApiRequestError } from "@src/packages/api/api-error";
import { useStytch } from "@louise/api/useStytch";
import { useMutation } from "@tanstack/react-query";

const API_BASE_URL = process.env.EXPO_PUBLIC_API_BASE_URL!;

/**
 * Requests account access deactivation.
 *
 * The endpoint name is a legacy backend contract. Its account action must
 * disable Louise/Stytch access without purging the patient record, since some
 * records may remain linked to clinics or other care relationships. The
 * backend owns the account-state operation; the client only clears its local
 * session after the request succeeds.
 */
export function useRequestAccountDeactivation() {
  const stytch = useStytch();
  const { tenantId, patientId } = useApiClient();

  return useMutation({
    mutationFn: async () => {
      if (!patientId) {
        throw new Error("No patient account to deactivate");
      }

      const token = stytch.session.getTokens()?.session_jwt;
      const response = await fetch(
        `${API_BASE_URL}/${tenantId}/patients/${patientId}/request-deletion`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );

      if (!response.ok) {
        throw new ApiRequestError(
          `Account deactivation failed (${response.status})`,
          {
            status: response.status,
            context: "useRequestAccountDeactivation",
          },
        );
      }
    },
  });
}
